import React from "react";
import { observer } from "mobx-react";
import ReactApexChart from "react-apexcharts";
import {
  baseChartOptions,
  axisLabelStyle,
  emphasisColors,
  largestIndex,
  axisFormatter
} from "../visuals/chartTheme";
import { formatMetric } from "../visuals/formatMetric";
import { useTokenHost } from "../visuals/cssVar";

/**
 * Magnitude across categories, with EMPHASIS colouring by default.
 *
 * One bar carries the point and the rest are grey context. That is what makes
 * a bar chart say something — six bars in six hues implies six unrelated
 * categories and gives the eye nothing to land on.
 *
 * `highlight` names the row to accent; without it the largest value wins,
 * which is the usual answer to "which channel/step/rep matters".
 * `highlight: null` explicitly opts out and uses one flat hue.
 *
 * Horizontal by default: category labels read left-to-right without rotation,
 * which is why every reference dashboard uses it for named categories.
 */
const BarChartBlock = observer(({ block }) => {
  // `ready` forces one re-render after mount so token reads see a live ref.
  const [rootRef, ready] = useTokenHost();
  const rows = (block.rows || []).filter(
    row => row && row.value != null && Number.isFinite(Number(row.value))
  );
  if (!rows.length) return null;

  // Sorted descending unless the category order is inherently meaningful
  // (months, funnel stages) — then the order IS information.
  const ordered = block.preserveOrder ? rows : [...rows].sort((a, b) => b.value - a.value);
  const values = ordered.map(row => Number(row.value));
  const unit = block.unit || "";

  const highlightIndex =
    block.highlight === null
      ? -1
      : block.highlight
        ? ordered.findIndex(row => row.label === block.highlight)
        : largestIndex(values);

  const base = baseChartOptions(rootRef.current);
  const horizontal = block.horizontal !== false;

  const options = {
    ...base,
    chart: { ...base.chart, type: "bar" },
    plotOptions: {
      bar: { horizontal, borderRadius: 3, barHeight: "62%", columnWidth: "58%", distributed: true }
    },
    colors: emphasisColors(rootRef.current, ordered.length, highlightIndex),
    xaxis: {
      categories: ordered.map(row => row.label),
      labels: {
        ...axisLabelStyle(rootRef.current),
        formatter: horizontal ? axisFormatter(unit) : undefined
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        ...axisLabelStyle(rootRef.current),
        formatter: horizontal ? undefined : axisFormatter(unit)
      }
    },
    grid: {
      ...base.grid,
      // Gridlines belong on the measured axis, not the category axis.
      xaxis: { lines: { show: horizontal } },
      yaxis: { lines: { show: !horizontal } }
    },
    tooltip: { ...base.tooltip, y: { formatter: value => formatMetric(value, { unit }) } }
  };

  return (
    <div className="cpVisual" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ReactApexChart
        key={ready ? "themed" : "initial"}
        options={options}
        series={[{ name: block.seriesName || "Value", data: values }]}
        type="bar"
        height={horizontal ? Math.max(180, ordered.length * 44) : 260}
      />
    </div>
  );
});

export default BarChartBlock;
